import { useCallback, useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { FileInput, TextInput, SelectInput, CheckboxInput } from '../inputs';
import {
  useDispatch,
  addResource,
  uploadFiles,
  deleteResource,
  fetchResourceById,
} from '../../../state';
import { useRefresh, useGetUsersData, useNotify } from '../../../hooks';
import {
  convertNullFieldsToEmptyString,
  generateDocumentsApi,
} from '../../../lib';
import { DocumentAPI, FileType } from '../../../types';

interface DocumentState {
  name: string;
  description: string;
  documentFeeType: string;
  quantity: number;
  billableToCustomer: boolean;
  visibleToCustomer: boolean;
  orderID?: string | number;
  [key: string]: any;
}

interface DocumentFeeOption {
  id: string;
  name: string;
}

interface DocumentFeeOption {
  id: string;
  name: string;
}

interface OrderDocumentUploadProps {
  open: boolean;
  handleClose: () => void;
  orderId?: string | number | null;
  documentId?: string | number | null;
  customerId?: string | number;
  getFiles?: (newFiles: Array<FileType>) => any;
  onSaveSuccess?: (documentFeeType: string) => void;
}

const initialState: DocumentState = {
  name: '',
  description: '',
  documentFeeType: '',
  quantity: 1,
  billableToCustomer: true,
  visibleToCustomer: true,
};

/**
 * Component for uploading documents to an order
 */
export function OrderDocumentUpload({
  open,
  handleClose,
  orderId,
  documentId,
  customerId,
  getFiles,
  onSaveSuccess,
}: OrderDocumentUploadProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const notify = useNotify();

  const { isCustomer, isProcessor } = useGetUsersData();

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [files, setFiles] = useState<FileType[]>([]);
  const [state, setState] = useState<DocumentState>(initialState);
  const [configuredDocumentFees, setConfiguredDocumentFees] = useState<
    DocumentFeeOption[]
  >([]);

  useEffect(() => {
    (async function () {
      if (documentId && orderId) {
        const { data } = await dispatch(
          fetchResourceById({
            baseUrl: '/orders',
            id: orderId,
            path: `documents/${documentId}`,
          }),
        );
        if (data) {
          const { document, id, documentFeeType, quantity, ...otherData } =
            data;
          const { name, description, ...rest } = document || {};
          setState((cur) => ({
            ...cur,
            ...rest,
            ...convertNullFieldsToEmptyString({
              name,
              description,
              ...otherData,
            }),
            quantity: quantity ?? 1,
            documentFeeType: documentFeeType
              ? documentFeeType
              : isCustomer
                ? ''
                : 'Other',
          }));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, documentId, dispatch]);

  useEffect(() => {
    (async function () {
      if (customerId) {
        const { data } = await dispatch(
          fetchResourceById({
            baseUrl: '/customers',
            id: customerId,
            path: `configured-document-fees/basic`,
          }),
        );
        let docOptions: DocumentFeeOption[] = [];
        if (!isCustomer) {
          docOptions.push({ id: 'Other', name: 'Other' });
        }
        if (data) {
          docOptions = [...data, ...docOptions];
        }
        setConfiguredDocumentFees(docOptions);
      }
    })();
  }, [customerId, dispatch, isCustomer]);

  // useEffect(() => {
  //   if (files.length) {
  //     const { rawFile: { name } = {} } = files[0];
  //     if (name) {
  //       formik.setFieldValue('name', name);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [files]);

  const handleSubmit = useCallback(
    async (values: DocumentState) => {
      if (!documentId && !files.length) {
        return notify('Please upload a file', 'error');
      }
      if (typeof getFiles === 'function') {
        const filesWithMetadata = files.map((f) => ({
          ...f,
          documentMetadata: {
            documentFeeType: values.documentFeeType,
            quantity: values.quantity,
            name: values.name,
            description: values.description,
            billableToCustomer: values.billableToCustomer,
            visibleToCustomer: values.visibleToCustomer,
          },
        }));
        getFiles(filesWithMetadata);
        handleClose();
        return;
      }
      if (!orderId) {
        return;
      }
      setSubmitting(true);
      let googleResponse: any,
        signedUrlsMapping: Record<string, string> = {};

      if (!documentId) {
        const { data, error: uploadError } = await dispatch(uploadFiles(files));
        if (uploadError) {
          return setSubmitting(false);
        }
        googleResponse = data?.googleResponse;
        signedUrlsMapping = data?.signedUrlsMapping ?? {};
      }
      const payload = generatePayload({
        orderId,
        files,
        signedUrlsMapping,
        values,
        hasUpload: !documentId,
      });
      if (documentId || googleResponse) {
        const { error } = await dispatch(
          addResource({
            baseUrl: `/orders/${orderId}/documents`,
            payload,
            message: 'Changes saved',
          }),
        );
        setSubmitting(false);
        if (!error) {
          refresh();
          onSaveSuccess?.(values.documentFeeType);
          handleClose();
        }
      }
    },
    [
      dispatch,
      documentId,
      files,
      getFiles,
      handleClose,
      notify,
      onSaveSuccess,
      orderId,
      refresh,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!documentId) {
      return;
    }
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: `/orders/${orderId}/documents`,
        id: documentId,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      refresh();
      handleClose();
    }
  }, [dispatch, documentId, handleClose, orderId, refresh]);

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Upload document</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
      isEdit={!!documentId}
      handleDelete={handleDelete}
    >
      <TextInput
        name='name'
        formikProps={formik}
        label='Name'
        className={classes.input}
        required
        disabled={isProcessor}
      />
      <SelectInput
        name='documentFeeType'
        formikProps={formik}
        label='Document type'
        className={classes.input}
        options={configuredDocumentFees}
        disabled={isProcessor}
        required
      />
      <TextInput
        name='description'
        formikProps={formik}
        label='Description'
        className={classes.input}
        disabled={isProcessor}
      />
      {formik.values.documentFeeType !== 'Other' && !isCustomer && (
        <TextInput
          name='quantity'
          formikProps={formik}
          label='Billable pages'
          className={classes.input}
          type='number'
          required
          slotProps={{ htmlInput: { min: 1 } }}
        />
      )}
      {!isCustomer && !isProcessor && (
        <CheckboxInput
          formikProps={formik}
          checkboxes={[
            {
              labelProps: {
                label: 'Billable to customer',
              },
              checkboxProps: {
                name: 'billableToCustomer',
              },
            },
          ]}
        />
      )}
      {!isCustomer && (
        <CheckboxInput
          formikProps={formik}
          checkboxes={[
            {
              labelProps: {
                label: 'Visible to customer',
              },
              checkboxProps: {
                name: 'visibleToCustomer',
              },
            },
          ]}
        />
      )}
      {!documentId && (
        <FileInput
          files={files}
          setFiles={setFiles}
          filePrefix={`orders/${orderId}-`}
          multiple
        />
      )}
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    marginBottom: 16,
    width: 300,
  },
}));

interface GeneratePayloadParams {
  hasUpload: boolean;
  orderId: string | number;
  files: FileType[];
  signedUrlsMapping: Record<string, string>;
  values: DocumentState;
}

function generatePayload({
  hasUpload,
  orderId,
  files,
  signedUrlsMapping,
  values,
}: GeneratePayloadParams) {
  const {
    documentFeeType,
    orderID,
    quantity,
    billableToCustomer,
    name,
    description,
    ...rest
  } = values;

  const Document = rest as DocumentAPI;

  if (hasUpload && files.length > 0) {
    const { key, rawFile } = files[0];
    Document.objectName = key;
    Document.url = signedUrlsMapping[key];
    Document.type = rawFile?.type || '';
  }

  const payload: Record<string, any> = {
    orderID: orderId,
    documents: [],
  };

  if (documentFeeType && documentFeeType !== 'Other') {
    payload.documentFeeType = documentFeeType;
    payload.quantity = quantity;
  }

  payload.billableToCustomer = billableToCustomer;

  if (hasUpload) {
    payload.documents = generateDocumentsApi({
      signedUrlsMapping: signedUrlsMapping,
      files,
      name,
      description,
    });
  } else {
    payload.documents = [{ description, name, ...rest }];
  }

  return payload;
}

const schema = object().shape({
  name: string().required('Required'),
  documentFeeType: string().required('Required'),
  description: string().nullable(),
  quantity: string()
    .nullable()
    .when('documentFeeType', {
      is: (documentFeeType: string) => documentFeeType !== 'Other',
      then: string().nullable().required('Required'),
    }),
});
